import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AdBlock from "@/components/AdBlock";
import api from "@/lib/api";

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${u}`;
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Blog & Guides — Giprompthub";
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
    }
    m.setAttribute(
      "content",
      "In-depth guides on AI prompting techniques, Midjourney, and ChatGPT prompt engineering from the Giprompthub team.",
    );

    api
      .get("/blog")
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      <main className="max-w-[1200px] mx-auto px-5 md:px-10 pt-16 pb-20">
        <div className="font-mono-label text-[#00E5FF] mb-4">Resources</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">
          Blog &amp; Guides
        </h1>
        <p className="mt-5 text-white/60 text-lg leading-relaxed max-w-2xl">
          Practical, no-fluff guides on AI prompting techniques, Midjourney,
          and ChatGPT prompt engineering — written by the team that tests
          every prompt in our library.
        </p>

        <div className="mt-10">
          <AdBlock variant="banner" testId="ad-blog-banner" />
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-white/40" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-24 text-white/50" data-testid="blog-empty">
            No articles published yet.
          </div>
        ) : (
          <div
            className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6"
            data-testid="blog-post-grid"
          >
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="group rounded-2xl border border-white/10 bg-[#0E0E12] overflow-hidden hover:border-[#00E5FF]/40 transition-colors flex flex-col"
              >
                <div className="aspect-[16/10] overflow-hidden bg-black/40">
                  <img
                    src={resolveUrl(post.cover_image_url)}
                    alt={post.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <span className="font-mono-label text-[#00E5FF]">
                    {post.category}
                  </span>
                  <h2 className="font-display text-lg mt-2 leading-snug">
                    {post.title}
                  </h2>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed flex-1">
                    {post.excerpt}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-white/80 group-hover:text-[#00E5FF] transition-colors">
                    Read guide
                    <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
